import classNames from "classnames";
import { WireNavigationModel } from "common/wire";
import { WireBackend } from "webapp/hydrated-ui/types";
import React from "react";
import ReactResizeDetector from "react-resize-detector";
import { BrowserContainer } from "sharedUI/ui/views/browser-container/browser-container";
import { WireApp } from "./wire-app";
import "twin.macro";
import { getSerializedAppSchema, IconImage, SerializedApp } from "common/components/SerializedApp";
// eslint-disable-next-line glide/no-out-of-bounds-view-imports
import { DesktopNavBar } from "webapp/chrome/smart/desktop-container/private/desktop-nav-bar";
import { GlideIcon } from "sharedUI/ui/views/glide-icon/glide-icon";
import { useWireBackend } from "hooks/use-wire-backend";
import { DataStore } from "common/components/types";
import { useHistory, useLocation } from "react-router-dom";
import { styled } from "webapp/styled-components";
import chroma from "chroma-js";
import { definedMap } from "@glideapps/ts-necessities";

interface Props {
    readonly navModel: WireNavigationModel;
    readonly backend: WireBackend;
    readonly serializedApp: SerializedApp;
    readonly onBack: () => void;
}

interface PlayerProps {
    readonly appID: string;
    readonly dataStore: DataStore;
    readonly serializedApp: SerializedApp;
}

export const PlayerDesktopWireApp: React.VFC<PlayerProps> = p => {
    const { dataStore, appID, serializedApp } = p;
    const schema = React.useMemo(() => getSerializedAppSchema(serializedApp), [serializedApp]);
    const history = useHistory();
    const location = useLocation();
    const deepLink = location.pathname.split("/dl/", 2)[1] ?? "";

    const { backend, navModel } = useWireBackend(appID, serializedApp, undefined, schema, dataStore, false, deepLink);

    const current = navModel === undefined ? undefined : `/play/${appID}/dl/${navModel.urlPath}`;

    React.useEffect(() => {
        if (current !== undefined && window.location.pathname.endsWith(current) === false) {
            history.push(current);
        }
    }, [current, history]);

    React.useEffect(() => {
        backend?.urlPathChanged(deepLink);
    }, [backend, deepLink]);

    if (backend === undefined || navModel === undefined) return null;

    return (
        <InnerWireApp
            backend={backend}
            iconImage={serializedApp.iconImage}
            navModel={navModel}
            title={serializedApp.title}
        />
    );
};

interface InnerProps {
    readonly navModel: WireNavigationModel;
    readonly backend: WireBackend;
    readonly title: string;
    readonly iconImage?: IconImage;
}

const InnerStyle = styled.div`
    .accent-bg {
        background: linear-gradient(
            90deg,
            ${p => chroma(p.theme.primaryAccentColor).alpha(1).css()} 0%,
            ${p => chroma(p.theme.primaryAccentColor).darken(0.5).alpha(1).css()} 100%
        );
    }
`;

const InnerWireApp: React.VFC<InnerProps> = p => {
    const { navModel, backend, title, iconImage } = p;

    const currentScreen = navModel.screen;

    return (
        <div tw="absolute inset-0 min-width[320px]">
            <InnerStyle tw="absolute inset-0 bg-bg-back overflow-y-auto pointer-events-auto flex-col flex">
                <ResponsiveProvider id="nav-root" tw="z-10 flex flex-col items-center sticky top-0">
                    <DesktopNavBar
                        tw="w-full"
                        title={title}
                        icon={iconImage}
                        tabs={navModel.tabs}
                        signedInUserEmail={navModel.userProfileButton?.signedInUserEmail}
                        signedInUserName={navModel.userProfileButton?.signedInUserName}
                        onUserButtonPressed={definedMap(
                            navModel.userProfileButton?.userProfileAction?.token,
                            x => () => backend.runAction(x, false)
                        )}
                        selected={navModel.tabs.find(x => x.isActive)}
                        onSelectionChange={x => backend.runAction(x.action.token, false)}
                        onSignInPressed={definedMap(
                            navModel.signInAction?.token,
                            x => () => backend.runAction(x, false)
                        )}
                    />
                </ResponsiveProvider>
                <main tw="flex flex-grow min-h-0">
                    <ResponsiveProvider id="main-root" tw="relative flex justify-center flex-1">
                        <div tw="relative all-child:flex-shrink-0 flex flex-col w-full">
                            <WireApp currentScreen={currentScreen} backend={backend} />
                        </div>
                    </ResponsiveProvider>
                </main>
            </InnerStyle>
            {navModel.modal !== undefined && (
                <div tw="absolute inset-0 flex justify-center bg-n300A z-20 pt-20">
                    <div tw="relative width[640px]">
                        <button
                            tw="flex justify-center items-center absolute right-2 top-2 p-2 bg-n0 rounded-full z-10"
                            onClick={() =>
                                navModel.modal?.closeAction &&
                                backend.runAction(navModel.modal.closeAction?.token, false)
                            }>
                            <GlideIcon kind="stroke" icon="st-close" iconSize={24} />
                        </button>
                        <div tw="w-full max-h-full bg-bg-front overflow-y-auto rounded-lg pt-6">
                            <ResponsiveProvider id="modal-root" tw="w-full">
                                <div role="dialog" tw="all-child:flex-shrink-0 flex flex-col max-w-screen-md relative">
                                    <WireApp currentScreen={navModel.modal} backend={backend} />
                                </div>
                            </ResponsiveProvider>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export const DesktopWireApp: React.VFC<Props> = p => {
    const { navModel, backend, serializedApp, onBack } = p;

    return (
        <BrowserContainer onBack={onBack} url={navModel.urlPath}>
            <InnerWireApp
                backend={backend}
                navModel={navModel}
                title={serializedApp.title}
                iconImage={serializedApp.iconImage}
            />
        </BrowserContainer>
    );
};

const RStyle = styled.div<{ maxWidth: number }>`
    > * {
        max-width: ${p => p.maxWidth}px;
    }
`;

export const ResponsiveProvider: React.FC<{ id: string; className?: string }> = p => {
    return (
        <ReactResizeDetector handleWidth={true} resizableElementId={p.id}>
            {(width: number) => {
                let cname = "";
                let sizeClass: ResponsiveSizeClass | undefined;
                if (width > 400) {
                    cname += "gp-sm ";
                    sizeClass = "sm";
                }
                if (width > 640) {
                    cname += "gp-md ";
                    sizeClass = "md";
                }
                if (width > 768) {
                    cname += "gp-lg ";
                    sizeClass = "lg";
                }
                if (width > 1024) {
                    cname += "gp-xl ";
                    sizeClass = "xl";
                }
                if (width > 1280) {
                    cname += "gp-2xl ";
                    sizeClass = "2xl";
                }
                return (
                    <ResponsiveSizeContext.Provider value={sizeClass}>
                        <RStyle maxWidth={width} id={p.id} className={classNames(cname, p.className)}>
                            {p.children}
                        </RStyle>
                    </ResponsiveSizeContext.Provider>
                );
            }}
        </ReactResizeDetector>
    );
};

type ResponsiveSizeClass = "sm" | "md" | "lg" | "xl" | "2xl";

const ResponsiveSizeContext = React.createContext<ResponsiveSizeClass | undefined>(undefined);

export function useResponsiveSizeClass(): ResponsiveSizeClass | undefined {
    return React.useContext(ResponsiveSizeContext);
}
